import React, {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';
import {getActiveAccountProfileLicense} from 'src/openapi/admin/apis/AccountActiveLicenseApi';
import {LicenseType} from 'src/openapi/admin/models/LicenseType';
import {useAppDispatch} from 'src/store/hooks';

import ComboBox, {ComboboxGroup, ComboboxItem} from '@pb/kit-component-combo-box';

import {AccountPreset} from './useAccountPreset';
import {useAllPresets} from './useAllPresets';

type Props = {
    preset: AccountPreset,
    setPreset: (user: AccountPreset) => void,
    disabled?: boolean,
    accountId?: number,
    licenseType?: LicenseType,
    showValidationError?: boolean,
};

export const EntitlementPresetsCombobox: React.FC<Props> = props => {
    const {license: activeLicense} = useSelector(selectAccountDetails);
    const {preset, setPreset, disabled, accountId, licenseType, showValidationError} = props;
    const apiDispatch = useAppDispatch();

    const {presets, loading, getPresetByLicenseType} = useAllPresets();
    const [shown, setShown] = useState(false);
    const [systemPickPreset, setSystemPickPreset] = useState<AccountPreset | null>(null);

    const [systemPickInitialized, setSystemPickInitialized] = useState(false);
    const [licensePresetApplied, setLicensePresetApplied] = useState(false);

    useEffect(() => {
        if (!licenseType) {
            return;
        }
        if (loading || licensePresetApplied) {
            return;
        }
        const licenseMappedPreset = getPresetByLicenseType(licenseType);
        if (licenseMappedPreset && (!preset || preset.presetId !== licenseMappedPreset.presetId)) {
            setPreset(licenseMappedPreset);
        }
        setLicensePresetApplied(true);
    }, [licenseType, loading, getPresetByLicenseType, preset, setPreset, licensePresetApplied]);

    useEffect(() => {
        if (licenseType || systemPickInitialized) {
            return;
        }
        if (!activeLicense && accountId) {
            apiDispatch(getActiveAccountProfileLicense({accountId}));
            return;
        }
        if (activeLicense) {
            const suggested = getPresetByLicenseType(activeLicense.typeCode);
            setSystemPickPreset(suggested || null);
            setSystemPickInitialized(true);
        }
    }, [licenseType, systemPickInitialized, activeLicense, accountId, apiDispatch, getPresetByLicenseType]);

    const onChangePreset = useCallback((selectedPreset: AccountPreset) => {
        setPreset(selectedPreset);
    }, [setPreset]);

    const allAvailablePresets = systemPickPreset
        ? [systemPickPreset, ...presets.filter(item => item.presetId !== systemPickPreset.presetId)]
        : presets;

    const matchedPreset = preset?.presetId
        ? allAvailablePresets.find(item => item.presetId === preset.presetId) || preset
        : preset;

    const isPresetNotSelected = !licenseType && (!matchedPreset || !matchedPreset.presetId);
    const shouldShowError = !loading && isPresetNotSelected && showValidationError;

    const isCNLicenseWithPreset = Boolean(activeLicense?.typeCode === LicenseType.CN && matchedPreset?.presetId);
    const isComboBoxDisabled = Boolean(disabled) || loading || isCNLicenseWithPreset;

    return (
        <div>
            <ComboBox<AccountPreset, AccountPreset>
                id="entitlement-suggest"
                value={matchedPreset}
                shown={shown}
                setShown={setShown}
                onClick={() => setShown(true)}
                onChange={onChangePreset}
                disabled={isComboBoxDisabled}
                placeholder={loading ? 'Loading presets...' : 'Select entitlement preset'}
                color={shouldShowError ? 'ERROR' : 'DEFAULT'}
                helperText={shouldShowError ? 'Please select an entitlement preset' : undefined}>
                {systemPickPreset ? (
                    <ComboboxGroup caption="System pick">
                        <ComboboxItem
                            key={systemPickPreset.presetId}
                            value={systemPickPreset}
                            caption={systemPickPreset.presetName}
                        />
                    </ComboboxGroup>
                ) : null}
                <ComboboxGroup caption="Available presets">
                    {presets
                        .filter(item => systemPickPreset ? item.presetId !== systemPickPreset.presetId : true)
                        .map(item => (
                            <ComboboxItem key={String(item.presetId)} value={item} caption={item.presetName}/>
                        ))}
                </ComboboxGroup>
            </ComboBox>
        </div>
    );
};
